<script setup>

import {computed, ref, onMounted, onUnmounted} from "vue";
import {getBreakPoint} from "@/Composables/UseBreakPoints.js";
import Badge from "primevue/badge";
import DarkModeButton from "@/Components/account/DarkModeButton.vue";
import Menubar from "primevue/menubar";
import {usePage} from "@inertiajs/vue3";
import CompanyLogo from "@/Components/CompanyLogo.vue";

const props = defineProps({
    items: Array,
    mobileItems: {
        type: Array,
        default: () => []
    },
    useBordexLogo: {
        type: Boolean,
        default: true
    },
    logoUrl: {
        type: String,
        default: null
    }
});

const page = usePage();
const isConfiguratorUser = page.props.isConfiguratorUser;
const isAdminDomain = page.props.currentDomain === 'admin';
const showDarkModeButton = !isConfiguratorUser && !isAdminDomain;

const isMobile = ref(false);
const breakpoint = 1024;

// Check if viewport is mobile
const checkMobile = () => {
    isMobile.value = window.innerWidth <= breakpoint;
};

onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
});

// Combine regular items with mobile-only items for the hamburger menu
// Only add mobile items when we're actually on mobile
const combinedItems = computed(() => {
    if (!isMobile.value || !props.mobileItems || props.mobileItems.length === 0) {
        return props.items;
    }

    return [
        ...props.items,
        { separator: true }, // Visual separator between regular and mobile items
        ...props.mobileItems
    ];
});
</script>

<template>
    <Menubar :pt="{
            rootList: {
                    class:'ml-6'
                }
        }" :breakpoint="breakpoint + 'px'" class="col-12" :model="combinedItems">
        <template #start>
            <CompanyLogo :logoUrl="logoUrl" :useBordexLogo="useBordexLogo" class="ml-2 mr-5"/>
        </template>
        <template class="md:ml-4" #item="{ item, props, hasSubmenu, root }">
            <!-- Component-based menu item (for mobile items with Vue components) -->
            <div v-if="item.component" class="w-full component-menu-item">
                <component :is="item.component" v-bind="item.componentProps || {}"/>
            </div>
            <!-- Regular link menu item -->
            <a v-else-if="item.href" v-ripple class="flex items-center" v-bind="props.action" :href="item.href">
                <span class="ml-2">{{ item.label }}</span>
                <Badge v-if="item.badge" :class="{ 'ml-auto': !root, 'ml-2': root }" :value="item.badge"/>
                <span v-if="item.shortcut && !isMobile"
                      class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-sm p-2">{{
                        item.shortcut
                    }}</span>
                <i v-if="hasSubmenu"
                   :class="['pi pi-angle-down', { 'pi-angle-down ml-2': root, 'pi-angle-right ml-auto': !root }]"></i>
            </a>
            <!-- Regular command menu item -->
            <a v-else v-ripple class="flex items-center cursor-pointer" v-bind="props.action">
                <span class="ml-2">{{ item.label }}</span>
                <Badge v-if="item.badge" :class="{ 'ml-auto': !root, 'ml-2': root }" :value="item.badge"/>
                <span v-if="item.shortcut && !isMobile"
                      class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-sm p-2">{{
                        item.shortcut
                    }}</span>
                <i v-if="hasSubmenu"
                   :class="['pi pi-angle-down', { 'pi-angle-down ml-2': root, 'pi-angle-right ml-auto': !root }]"></i>
            </a>
        </template>

        <template #end>
            <div class="flex items-center gap-3 configurator-menubar-end">
                <slot name="end"/>
                <DarkModeButton v-if="showDarkModeButton"/>
            </div>
        </template>
    </Menubar>
</template>

<style lang="scss">
.p-menubar-mobile {
    justify-content: space-between;
}

.p-menubar-start {
    min-width: 10% !important;
}

.p-menubar {
    border: none !important;
    border-radius: 0 !important;
    margin-bottom: 1rem !important;
    box-shadow: 0 4px 2px -2px rgba(0, 0, 0, .1) !important;
    height: 70px;

    a {
        color: var(--textColor);
    }

    .p-menubar-end {
        padding-right: 0;
        flex-shrink: 0;
    }
}

.p-menubar-mobile > div.p-menubar-start {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
    width: auto !important;
    overflow: hidden;

    div.logo-holder {
        max-width: 120px;
        width: auto;
    }
}

@media screen and (max-width: $md) {
    .p-menubar {
        height: 56px;
        min-height: 56px;
    }

    .configurator-menubar-end .p-button {
        min-height: 44px;
        font-size: 1rem;
    }
}

:deep(.admin-menu-mobile-section-label .p-menubar-item-link) {
    opacity: 1;
    cursor: default;
    pointer-events: none;
    min-height: auto;
    padding-top: 0.75rem;
    padding-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

/* Separator styling for light mode */
:deep(.p-menubar-separator) {
    border-top: 1px solid var(--p-surface-200) !important;
    margin: 0.5rem 0 !important;
}

/* Separator styling for dark mode */
.configurator-dark :deep(.p-menubar-separator) {
    border-top: 1px solid var(--p-surface-700) !important;
}

/* Ensure menu items in mobile overlay have proper text color in dark mode */
.configurator-dark :deep(.p-menubar-root-list .p-menubar-item-link) {
    color: var(--p-text-color) !important;
}

.configurator-dark :deep(.p-menubar-root-list .p-menubar-item-link .p-menubar-item-label) {
    color: var(--p-text-color) !important;
}

/* Component menu items - prevent overflow */
.component-menu-item {
    width: 100%;
    box-sizing: border-box;
    padding: 0.5rem 0.5rem;
    overflow: hidden;
}

/* Ensure mobile menu doesn't overflow on small screens */
:deep(.p-menubar-mobile-active .p-menubar-root-list) {
    width: 100% !important;
    max-width: 100vw !important;
    box-sizing: border-box !important;
    padding: 0.75rem !important;
    overflow-x: hidden !important;
    left: 0 !important;
    right: 0 !important;
}

:deep(.p-menubar-mobile-active .p-menubar-item) {
    width: 100% !important;
    box-sizing: border-box !important;
}

/* Ensure components inside menu items respect container width */
:deep(.p-menubar-mobile-active .component-menu-item > *) {
    max-width: 100% !important;
    box-sizing: border-box !important;
}

/* Override any potential overflow from PrimeVue */
:deep(.p-menubar .p-menubar-root-list) {
    overflow-x: hidden !important;
}

/* Ensure the mobile overlay panel itself is constrained */
:deep(.p-menubar-mobile-active) {
    width: 100% !important;
    max-width: 100% !important;
    left: 0 !important;
    right: 0 !important;
    box-sizing: border-box !important;
}

/* Additional constraint for the menu panel */
:deep(.p-menubar-mobile-active .p-menubar-end) {
    display: none !important;
}

</style>
